import { useEffect, useState, type FormEvent } from 'react';
import { useOp } from '../../api.ts';
import type { MerchantWorkspace } from '../../types.ts';
import { runWrite, useChanges } from '../../ui/changes.ts';
import { useSiteDraft } from '../../ui/site-draft.ts';
import { ErrorBox, Loading } from '../../ui/kit.tsx';
import { useToast } from '../../ui/toasts.tsx';
import type { CategoryTree, SeoBlock, SiteContent } from './types.ts';
import { Counter, resolveVariables } from './ui.tsx';

const VARIABLE_HELP = ['merchant', 'month', 'year', 'coupon_count', 'code_count', 'top_coupon_savings', 'last_updated'];

/** Catalog panels added to the merchant workspace: per-site content, and name/slug/categories. */
export function MerchantCatalogPanels({ workspace }: { workspace: MerchantWorkspace }) {
  return (
    <>
      <SiteContentPanel workspace={workspace} />
      <IdentityAndCategories workspace={workspace} />
    </>
  );
}

function SiteContentPanel({ workspace }: { workspace: MerchantWorkspace }) {
  const [site, setSite] = useState(workspace.sites[0]?.propertyRef ?? '');
  const changes = useChanges();
  const content = useOp<SiteContent>('admin.merchants.siteContent', { ref: workspace.ref, propertyRef: site }, changes, Boolean(site));
  if (!site) return null;
  return (
    <article className="card span-2" aria-labelledby="content-title">
      <header className="card-head">
        <h2 id="content-title">Page content</h2>
        <div className="segmented" role="group" aria-label="Site">
          {workspace.sites.map((s) => (
            <button key={s.propertyRef} type="button" aria-pressed={site === s.propertyRef} onClick={() => setSite(s.propertyRef)}>
              {s.propertyName}
            </button>
          ))}
        </div>
      </header>
      {content.error ? <ErrorBox error={content.error} onRetry={content.reload} /> : null}
      {!content.data && content.loading ? <Loading /> : null}
      {content.data && content.data.propertyRef === site ? <SiteContentEditor key={site} data={content.data} /> : null}
    </article>
  );
}

interface ContentDraft {
  description: string;
  metaTitle: string;
  metaDescription: string;
  blocks: SeoBlock[];
}

/** One site's page content; its draft survives reloads caused by saves elsewhere (each site saves on its own). */
function SiteContentEditor({ data }: { data: SiteContent }) {
  const toast = useToast();
  const form = useSiteDraft<ContentDraft>({ description: data.description, metaTitle: data.metaTitle ?? '', metaDescription: data.metaDescription ?? '', blocks: data.seoText });
  const { description, metaTitle, metaDescription, blocks } = form.draft;
  const saving = form.saving;
  const setDescription = (value: string) => form.setDraft({ ...form.draft, description: value });
  const setMetaTitle = (value: string) => form.setDraft({ ...form.draft, metaTitle: value });
  const setMetaDescription = (value: string) => form.setDraft({ ...form.draft, metaDescription: value });
  const setBlocks = (value: SeoBlock[]) => form.setDraft({ ...form.draft, blocks: value });
  const disabled = !data.canEdit;
  const v = data.variables;

  const update = (index: number, patch: Partial<SeoBlock>) => setBlocks(blocks.map((b, i) => (i === index ? ({ ...b, ...patch } as SeoBlock) : b)));
  const move = (index: number, delta: number) => {
    const next = [...blocks];
    const [item] = next.splice(index, 1);
    next.splice(Math.max(0, Math.min(next.length, index + delta)), 0, item!);
    setBlocks(next);
  };
  const add = (type: SeoBlock['type']) =>
    setBlocks([...blocks, type === 'heading' ? { type, level: 2, text: '' } : type === 'faq' ? { type, question: '', answer: '' } : { type, text: '' }]);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    await form.save((d) =>
      runWrite<{ changed: string[]; warnings: string[] }>(
        toast,
        'admin.merchants.updateSiteContent',
        { ref: data.ref, propertyRef: data.propertyRef, description: d.description, metaTitle: d.metaTitle, metaDescription: d.metaDescription, seoText: d.blocks },
        (r) => (r.changed.length ? `Saved ${r.changed.join(', ')}${r.warnings.length ? ` · ${r.warnings[0]}` : ''}` : 'Nothing changed'),
      ),
    );
  };

  return (
    <form onSubmit={save} className="site-content">
      <div className={`indexing ${data.indexing.effective ? 'indexing-yes' : 'indexing-no'}`} role="status">
        <strong>{data.indexing.effective ? 'Indexed' : 'Noindex'}</strong> <span className="muted">({data.indexing.setting === 'auto' ? 'automatic' : `set to ${data.indexing.setting}`})</span>
        <ul>
          {data.indexing.reasons.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
        <small className="muted">Change it with “Indexable” in the site table above.</small>
      </div>
      <div className="content-grid">
        <div className="content-edit">
          <label>
            <span className="label-row">Merchant description on this site</span>
            <textarea rows={3} value={description} disabled={disabled} onChange={(e) => setDescription(e.target.value)} />
          </label>
          <label>
            <span className="label-row">
              Meta title <Counter value={resolveVariables(metaTitle, v)} limit={data.limits.metaTitle} />
            </span>
            <input value={metaTitle} disabled={disabled} placeholder="Empty = generated title" onChange={(e) => setMetaTitle(e.target.value)} />
          </label>
          <label>
            <span className="label-row">
              Meta description <Counter value={resolveVariables(metaDescription, v)} limit={data.limits.metaDescription} />
            </span>
            <textarea rows={2} value={metaDescription} disabled={disabled} placeholder="Empty = generated description" onChange={(e) => setMetaDescription(e.target.value)} />
          </label>
          <p className="muted small variable-help">
            Variables:
            {VARIABLE_HELP.map((name) => (
              <code key={name} title={v[name] ? `now: ${v[name]}` : 'empty right now'}>{`{${name}}`}</code>
            ))}
          </p>
          <h3 className="section-title">SEO text</h3>
          <p className="muted small">
            The page always adds a section on the live and recently expired codes, written from the coupon data on every visit; write here only the lasting text (and
            seasonal blocks with a "show until" date).
          </p>
          {blocks.length === 0 ? <p className="muted">No written text: the site shows generated text from the facts.</p> : null}
          <ol className="blocks">
            {blocks.map((block, index) => (
              <li key={index} className="block-row">
                <div className="block-tools">
                  <span className="badge badge-muted">{block.type === 'heading' ? `H${block.level}` : block.type === 'faq' ? 'FAQ' : 'Text'}</span>
                  {block.provenance && block.provenance !== 'human' ? <span className="badge badge-ai">{block.provenance === 'ai_auto' ? 'AI' : 'AI, approved'}</span> : null}
                  <label className="inline-field small" title="Seasonal text (Black Friday, Christmas): shown until this day, then it drops off the page by itself">
                    Show until
                    <input
                      type="date"
                      value={block.until ?? ''}
                      disabled={disabled}
                      onChange={(e) => update(index, { until: e.target.value || undefined } as Partial<SeoBlock>)}
                    />
                  </label>
                  {block.until && block.until < new Date().toISOString().slice(0, 10) ? <span className="badge badge-muted">ended, not shown</span> : null}
                  <button type="button" className="btn btn-small btn-ghost" disabled={disabled || index === 0} onClick={() => move(index, -1)} aria-label="Move up">
                    ↑
                  </button>
                  <button type="button" className="btn btn-small btn-ghost" disabled={disabled || index === blocks.length - 1} onClick={() => move(index, 1)} aria-label="Move down">
                    ↓
                  </button>
                  <button type="button" className="btn btn-small btn-ghost btn-danger-ghost" disabled={disabled} onClick={() => setBlocks(blocks.filter((_, i) => i !== index))} aria-label="Remove block">
                    Remove
                  </button>
                </div>
                {block.type === 'faq' ? (
                  <>
                    <input value={block.question} disabled={disabled} placeholder="Question" aria-label="Question" onChange={(e) => update(index, { question: e.target.value })} />
                    <textarea rows={2} value={block.answer} disabled={disabled} placeholder="Answer" aria-label="Answer" onChange={(e) => update(index, { answer: e.target.value })} />
                  </>
                ) : block.type === 'heading' ? (
                  <input value={block.text} disabled={disabled} aria-label="Heading" onChange={(e) => update(index, { text: e.target.value })} />
                ) : (
                  <textarea rows={3} value={block.text} disabled={disabled} aria-label="Paragraph" onChange={(e) => update(index, { text: e.target.value })} />
                )}
              </li>
            ))}
          </ol>
          {!disabled ? (
            <div className="form-actions">
              <button type="button" className="btn btn-small" onClick={() => add('heading')}>
                + Heading
              </button>
              <button type="button" className="btn btn-small" onClick={() => add('paragraph')}>
                + Paragraph
              </button>
              <button type="button" className="btn btn-small" onClick={() => add('faq')}>
                + FAQ
              </button>
            </div>
          ) : null}
        </div>
        <aside className="content-preview" aria-label="Preview with today’s values">
          <h3 className="section-title">Preview</h3>
          <div className="serp">
            <div className="serp-title">{resolveVariables(metaTitle, v) || <span className="muted">generated title</span>}</div>
            <div className="serp-desc">{resolveVariables(metaDescription, v) || <span className="muted">generated description</span>}</div>
          </div>
          {blocks.map((block, index) =>
            block.type === 'heading' ? (
              block.level === 2 ? <h4 key={index}>{resolveVariables(block.text, v)}</h4> : <h5 key={index}>{resolveVariables(block.text, v)}</h5>
            ) : block.type === 'faq' ? (
              <p key={index}>
                <strong>{resolveVariables(block.question, v)}</strong>
                <br />
                {resolveVariables(block.answer, v)}
              </p>
            ) : (
              <p key={index}>{resolveVariables(block.text, v)}</p>
            ),
          )}
        </aside>
      </div>
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={disabled || saving || !form.dirty}>
          {saving ? 'Saving…' : 'Save page content'}
        </button>
        <button type="button" className="btn btn-ghost" disabled={saving || !form.dirty} onClick={form.reset}>
          Reset
        </button>
        {form.dirty ? <span className="unsaved">Unsaved</span> : null}
      </div>
    </form>
  );
}

function IdentityAndCategories({ workspace }: { workspace: MerchantWorkspace }) {
  const toast = useToast();
  const changes = useChanges();
  const tree = useOp<CategoryTree>('admin.categories.tree', { merchantRef: workspace.ref }, changes);
  const [identity, setIdentity] = useState({ localName: workspace.name, slug: workspace.slug });
  const [chosen, setChosen] = useState<string[]>([]);
  useEffect(() => setIdentity({ localName: workspace.name, slug: workspace.slug }), [workspace.name, workspace.slug]);
  useEffect(() => setChosen((tree.data?.nodes ?? []).filter((n) => n.assigned).map((n) => n.ref)), [tree.data]);
  const disabled = !workspace.canEditFacts;

  const saveIdentity = (event: FormEvent) => {
    event.preventDefault();
    void runWrite<{ changed: string[] }>(toast, 'admin.merchants.updateIdentity', { ref: workspace.ref, ...identity }, (r) => (r.changed.length ? `Saved ${r.changed.join(', ')}` : 'Nothing changed'));
  };
  const saveCategories = () => runWrite(toast, 'admin.merchants.setCategories', { ref: workspace.ref, categoryRefs: chosen }, 'Categories saved');

  return (
    <article className="card" aria-labelledby="identity-title">
      <h2 id="identity-title">Name, URL and categories ({workspace.market})</h2>
      <form onSubmit={saveIdentity} className="form-grid">
        <label>
          Name
          <input value={identity.localName} disabled={disabled} onChange={(e) => setIdentity({ ...identity, localName: e.target.value })} />
        </label>
        <label>
          URL slug
          <input value={identity.slug} disabled={disabled} onChange={(e) => setIdentity({ ...identity, slug: e.target.value })} />
        </label>
        {identity.slug !== workspace.slug ? <p className="span-2 text-warn small">Changing the slug moves the merchant page to /{identity.slug}.</p> : null}
        <div className="span-2">
          <button type="submit" className="btn btn-small" disabled={disabled}>
            Save name and URL
          </button>
        </div>
      </form>
      <fieldset>
        <legend>Categories</legend>
        {tree.data && tree.data.nodes.length === 0 ? <p className="muted">No categories in {workspace.market} yet.</p> : null}
        <div className="category-checks">
          {(tree.data?.nodes ?? []).map((node) => (
            <label key={node.ref} className="inline-check" style={{ paddingLeft: node.depth * 14 }}>
              <input type="checkbox" disabled={disabled} checked={chosen.includes(node.ref)} onChange={(e) => setChosen(e.target.checked ? [...chosen, node.ref] : chosen.filter((r) => r !== node.ref))} />
              {node.name}
            </label>
          ))}
        </div>
        <button type="button" className="btn btn-small" disabled={disabled} onClick={saveCategories}>
          Save categories
        </button>
      </fieldset>
    </article>
  );
}
